<template>
  <div class="currently-working">
    <div class="page-header">
      <div class="page-title-wrap">
        <h1 class="page-title">En cours de travail</h1>
        <p class="page-subtitle">Présence du jour par groupe</p>
      </div>
      <button class="btn btn-refresh" @click="loadData" :disabled="loading">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ rotating: loading }">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
        Actualiser
      </button>
    </div>

    <div class="filter-container">
      <div class="filter-row">
        <div class="filter-group">
          <label class="filter-label">Groupe</label>
          <select v-model="selectedGroup" class="filter-select" @change="loadData">
            <option value="">Tous les groupes en cours</option>
            <option v-for="group in workGroups" :key="group.group_name" :value="group.group_name">
              {{ group.group_name }}
            </option>
          </select>
        </div>

        <div class="search-wrapper">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher un employé..."
            class="search-input"
          />
          <button v-if="searchQuery" @click="searchQuery = ''" class="clear-btn" type="button">x</button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-spinner">
      <div class="spinner"></div>
      <p>Chargement des données...</p>
    </div>

    <div v-else-if="errorMessage" class="error-alert">
      {{ errorMessage }}
    </div>

    <div v-else-if="filteredStaff.length === 0" class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
      <p>{{ emptyMessage }}</p>
    </div>

    <div v-else class="staff-container">
      <div class="staff-count">
        <strong>{{ filteredStaff.length }}</strong>
        <span>{{ selectedGroup ? 'employé(s) du groupe' : 'employé(s) en cours, tous groupes' }}</span>
      </div>

      <div class="working-mobile-list">
        <article
          v-for="staff in filteredStaff"
          :key="staff.id"
          class="working-mobile-card"
          :class="`status-${staff.status}`"
          role="button"
          tabindex="0"
          @click="openStaffActions(staff)"
          @keydown.enter.prevent="openStaffActions(staff)"
          @keydown.space.prevent="openStaffActions(staff)"
        >
          <div class="mobile-avatar">
            <img v-if="staff.pic" :src="'/api-pointage/uploads/' + staff.pic" :alt="staff.first_name" />
            <strong v-else>{{ firstLetter(staff) }}</strong>
          </div>
          <div class="working-mobile-info">
            <div class="mobile-title">
              <h3>{{ staff.first_name }} {{ staff.last_name }}</h3>
              <span class="status-pill" :class="`status-${staff.status}`">{{ statusLabel(staff) }}</span>
            </div>
            <span>{{ staff.group_name || 'Sans groupe' }}</span>
            <p>Entrée: {{ formatTime(staff.time_in) }}</p>
          </div>
        </article>
      </div>

      <div class="table-wrapper desktop-table">
        <table class="staff-table">
          <thead>
            <tr>
              <th class="col-pic">Photo</th>
              <th class="col-name">Employé</th>
              <th class="col-group">Groupe</th>
              <th class="col-status">Etat</th>
              <th class="col-time">Entrée</th>
              <th class="col-duration">Durée</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="staff in filteredStaff"
              :key="staff.id"
              class="staff-row"
              :class="`status-${staff.status}`"
            >
              <td class="col-pic">
                <div class="pic-cell">
                  <img v-if="staff.pic" :src="'/api-pointage/uploads/' + staff.pic" :alt="staff.first_name" class="staff-pic" />
                  <div v-else class="staff-pic-placeholder">
                    {{ firstLetter(staff) }}
                  </div>
                </div>
              </td>
              <td class="col-name">
                <div class="name-cell">
                  <strong>{{ staff.first_name }} {{ staff.last_name }}</strong>
                </div>
              </td>
              <td class="col-group">{{ staff.group_name || '-' }}</td>
              <td class="col-status">
                <span class="status-pill" :class="`status-${staff.status}`">{{ statusLabel(staff) }}</span>
              </td>
              <td class="col-time">{{ formatTime(staff.time_in) }}</td>
              <td class="col-duration">{{ staff.status === 'working' || staff.status === 'late' ? staff.duration : '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="auto-refresh-info">
      <small>Actualisation automatique chaque 30 secondes</small>
    </div>

    <div v-if="selectedStaff" class="action-modal-backdrop" @click.self="closeStaffActions">
      <section class="action-sheet" role="dialog" aria-modal="true" aria-label="Actions employé">
        <button class="sheet-close" type="button" @click="closeStaffActions" aria-label="Fermer">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        <div class="sheet-identity">
          <div class="sheet-avatar">
            <img v-if="selectedStaff.pic" :src="'/api-pointage/uploads/' + selectedStaff.pic" :alt="selectedStaff.first_name" />
            <strong v-else>{{ firstLetter(selectedStaff) }}</strong>
          </div>
          <div>
            <h2>{{ selectedStaff.first_name }} {{ selectedStaff.last_name }}</h2>
            <span class="status-pill" :class="`status-${selectedStaff.status}`">{{ statusLabel(selectedStaff) }}</span>
          </div>
        </div>

        <div class="sheet-actions">
          <a
            class="sheet-action call"
            :class="{ disabled: !selectedStaff.phone }"
            :href="selectedStaff.phone ? `tel:${cleanPhone(selectedStaff.phone)}` : undefined"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.62 2.61a2 2 0 0 1-.45 2.11L8.09 9.63a16 16 0 0 0 6.28 6.28l1.19-1.19a2 2 0 0 1 2.11-.45c.84.29 1.71.5 2.61.62A2 2 0 0 1 22 16.92Z" />
            </svg>
            <span>Appeler</span>
          </a>

          <button class="sheet-action dossier" type="button" @click="openPointageList">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M8 6h13" />
              <path d="M8 12h13" />
              <path d="M8 18h13" />
              <path d="M3 6h.01" />
              <path d="M3 12h.01" />
              <path d="M3 18h.01" />
            </svg>
            <span>Liste de pointage</span>
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppCurrentlyWorking',
  data() {
    return {
      staffWorkingList: [],
      workGroups: [],
      searchQuery: '',
      selectedGroup: '',
      loading: false,
      errorMessage: '',
      selectedStaff: null,
      autoRefreshInterval: null,
    };
  },
  computed: {
    filteredStaff() {
      let result = this.staffWorkingList;

      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        result = result.filter((staff) => {
          const fullName = `${staff.first_name || ''} ${staff.last_name || ''}`.toLowerCase();
          return fullName.includes(query);
        });
      }

      return result;
    },
    emptyMessage() {
      if (this.searchQuery.trim()) {
        return 'Aucun résultat pour cette recherche.';
      }
      if (this.selectedGroup) {
        return 'Aucun employé à afficher dans ce groupe.';
      }
      return 'Aucun employé en cours de travail actuellement.';
    },
  },
  methods: {
    async fetchGroups() {
      try {
        const response = await fetch('/api-pointage/fetchgroups', {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Failed');
        const data = await response.json();
        this.workGroups = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Error fetching groups:', error);
        this.workGroups = [];
      }
    },
    async loadData() {
      this.errorMessage = '';
      this.loading = true;
      try {
        let url = '/api-pointage/currentlyworking';
        if (this.selectedGroup) {
          url += `?group=${encodeURIComponent(this.selectedGroup)}`;
        }
        const response = await fetch(url, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        this.staffWorkingList = rows.filter((staff) => {
          if (this.selectedGroup) {
            return staff.status !== 'checked_out';
          }
          return staff.status === 'working' || staff.status === 'late';
        });
      } catch (error) {
        console.error('Error loading data:', error);
        this.errorMessage = 'Erreur lors du chargement des données';
      } finally {
        this.loading = false;
      }
    },
    parseServerDateTime(value) {
      if (!value) return null;
      const normalized = String(value).replace(/\//g, '-').replace(' ', 'T');
      const parsed = new Date(normalized);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    formatTime(timeString) {
      if (!timeString) return '-';
      const parsed = this.parseServerDateTime(timeString);
      if (!parsed) return timeString;
      const day = String(parsed.getDate()).padStart(2, '0');
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const year = parsed.getFullYear();
      const hour = String(parsed.getHours()).padStart(2, '0');
      const minute = String(parsed.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hour}:${minute}`;
    },
    firstLetter(staff) {
      return (staff.first_name || '?').charAt(0).toUpperCase();
    },
    statusLabel(staff) {
      if (staff.status_label) return staff.status_label;
      if (staff.status === 'working') return 'En cours';
      if (staff.status === 'late') return 'Retard';
      if (staff.status === 'not_pointed') return 'Pas encore pointé';
      return 'Déjà pointé';
    },
    openStaffActions(staff) {
      this.selectedStaff = staff;
    },
    closeStaffActions() {
      this.selectedStaff = null;
    },
    cleanPhone(phone) {
      return String(phone || '').replace(/[^\d+]/g, '');
    },
    openPointageList() {
      if (!this.selectedStaff) return;
      const staffId = this.selectedStaff.id;
      this.closeStaffActions();
      this.$router.push({ path: '/pointage', query: { staff_id: staffId } });
    },
  },
  async mounted() {
    await this.fetchGroups();
    await this.loadData();
    this.autoRefreshInterval = setInterval(() => {
      this.loadData();
    }, 30000);
  },
  beforeUnmount() {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  },
};
</script>

<style scoped>
.currently-working {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 15px;
}

.page-title-wrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
}

.page-title {
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  transition: all 0.2s ease;
}

.btn-refresh:hover:not(:disabled) {
  background-color: #1d4ed8;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.22);
}

.btn-refresh:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-refresh svg {
  width: 18px;
  height: 18px;
}

.btn-refresh svg.rotating {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.filter-container {
  margin-bottom: 18px;
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  border: 1px solid #dbe4f0;
}

.filter-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  align-items: stretch;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.filter-label {
  font-size: 11px;
  font-weight: 800;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0;
}

.filter-select {
  height: 42px;
  padding: 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  color: #0f172a;
  background: white;
  cursor: pointer;
}

.filter-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  height: 42px;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0 12px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.search-wrapper:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.search-icon {
  width: 17px;
  height: 17px;
  color: #64748b;
  flex-shrink: 0;
  margin-right: 8px;
}

.search-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 0;
  margin: 0;
  border: 0 !important;
  outline: 0;
  box-shadow: none !important;
  background: transparent;
  appearance: none;
  font-size: 14px;
  color: #0f172a;
}

.clear-btn {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 18px;
  padding: 0 2px;
}

.loading-spinner,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: #64748b;
  background: white;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #dbe4f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 14px;
}

.empty-state svg {
  width: 54px;
  height: 54px;
  color: #94a3b8;
  margin-bottom: 12px;
}

.empty-state p {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
}

.error-alert {
  padding: 13px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 6px;
  margin-bottom: 16px;
  font-weight: 700;
}

.staff-container {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.staff-count {
  background: white;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.staff-count span {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
}

.staff-count strong {
  font-size: 20px;
  color: #0f172a;
}

.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  border: 1px solid #dbe4f0;
}

.staff-table {
  width: 100%;
  border-collapse: collapse;
}

.staff-table thead {
  background: #f1f5f9;
}

.staff-table th {
  padding: 12px 14px;
  text-align: left;
  font-size: 12px;
  font-weight: 800;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0;
  border-bottom: 1px solid #dbe4f0;
}

.staff-table td {
  padding: 14px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  color: #0f172a;
}

.staff-row {
  background: white;
}

.staff-row.status-not_pointed {
  background: #fef2f2;
}

.staff-row.status-late {
  background: #fff7ed;
}

.staff-row.status-working {
  background: #f0fdf4;
}

.staff-row.status-checked_out {
  background: #f8fafc;
  color: #64748b;
}

.pic-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.staff-pic,
.staff-pic-placeholder,
.mobile-avatar {
  width: 58px;
  height: 58px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid #e2e8f0;
}

.staff-pic-placeholder,
.mobile-avatar {
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.name-cell strong {
  font-size: 15px;
  font-weight: 800;
  color: #111827;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 0;
  border-radius: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 800;
  text-align: center;
  line-height: 1.2;
}

.status-pill.status-working {
  background: transparent;
  color: #15803d;
}

.status-pill.status-late {
  background: transparent;
  color: #c2410c;
}

.status-pill.status-not_pointed {
  background: transparent;
  border: 0;
  color: #b91c1c;
}

.status-pill.status-checked_out {
  background: transparent;
  color: #475569;
}

.col-pic {
  width: 90px;
}

.col-status {
  width: 150px;
}

.col-time,
.col-duration {
  width: 170px;
}

.desktop-table {
  display: none;
}

.working-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.working-mobile-card {
  background: white;
  border: 1px solid #dbe4f0;
  border-left-width: 4px;
  border-radius: 8px;
  padding: 12px;
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  min-height: 104px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  text-align: left;
}

.working-mobile-card:active {
  transform: scale(0.99);
}

.working-mobile-card.status-working {
  border-left-color: #15803d;
}

.working-mobile-card.status-late {
  border-left-color: #ea580c;
}

.working-mobile-card.status-not_pointed {
  border-left-color: #dc2626;
}

.working-mobile-card.status-checked_out {
  border-left-color: #94a3b8;
  background: #f8fafc;
}

.mobile-avatar {
  flex-shrink: 0;
  width: 78px;
  height: 78px;
  border-radius: 10px;
  font-size: 26px;
  background: #f1f5f9;
  color: #2563eb;
  border-color: #dbe4f0;
}

.mobile-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.working-mobile-info {
  min-width: 0;
  flex: 1;
}

.mobile-title {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
}

.working-mobile-info h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 900;
  color: #111827;
  overflow-wrap: anywhere;
  line-height: 1.2;
}

.working-mobile-info span {
  display: inline-block;
  margin-top: 8px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
}

.working-mobile-info p {
  margin: 7px 0 0;
  color: #1e293b;
  font-size: 14px;
  font-weight: 800;
}

.action-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 14px;
}

.action-sheet {
  position: relative;
  width: min(100%, 420px);
  background: white;
  border-radius: 14px 14px 10px 10px;
  padding: 18px;
  box-shadow: 0 -12px 34px rgba(15, 23, 42, 0.2);
}

.sheet-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: 1px solid #dbe4f0;
  border-radius: 50%;
  background: white;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.sheet-close svg {
  width: 18px;
  height: 18px;
}

.sheet-identity {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  padding-right: 42px;
}

.sheet-avatar {
  width: 76px;
  height: 76px;
  border-radius: 10px;
  background: #f1f5f9;
  border: 1px solid #dbe4f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  font-size: 26px;
  font-weight: 900;
  overflow: hidden;
}

.sheet-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sheet-identity h2 {
  margin: 0 0 8px;
  color: #111827;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.sheet-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-top: 18px;
}

.sheet-action {
  min-height: 52px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-decoration: none;
  font-size: 15px;
  font-weight: 900;
  cursor: pointer;
}

.sheet-action svg {
  width: 20px;
  height: 20px;
}

.sheet-action.call {
  background: #ecfdf5;
  border-color: #bbf7d0;
  color: #166534;
}

.sheet-action.dossier {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.sheet-action.disabled {
  opacity: 0.45;
  pointer-events: none;
}

.auto-refresh-info {
  margin-top: 16px;
  text-align: center;
  color: #64748b;
}

@media (max-width: 900px) {
  .filter-row {
    grid-template-columns: 1fr;
  }

  .working-mobile-card {
    grid-template-columns: 78px minmax(0, 1fr);
    min-height: 104px;
  }

  .mobile-avatar {
    width: 78px;
    height: 78px;
  }
}

@media (max-width: 560px) {
  .currently-working {
    padding: 12px;
  }

  .page-header {
    align-items: stretch;
  }

  .page-title {
    font-size: 22px;
  }

  .btn-refresh {
    width: 100%;
    justify-content: center;
  }

  .mobile-title {
    gap: 6px;
  }

  .working-mobile-card {
    grid-template-columns: 74px minmax(0, 1fr);
    padding: 12px;
    gap: 12px;
  }

  .mobile-avatar {
    width: 74px;
    height: 74px;
    font-size: 25px;
  }
}
</style>
